import { useEffect, useState, useRef, RefObject, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../../components/feature/LanguageSwitcher';
import { useSEO } from '../../utils/seoHelpers';
// Declare UnicornStudio on window
declare global {
  interface Window {
    UnicornStudio?: {
      isInitialized?: boolean;
      init: () => void;
      destroy: () => void;
    };
  }
}

// Custom hook for scroll animations
function useScrollAnimation<T extends HTMLElement>(
  options: {
    threshold?: number | number[];
    root?: Element | null;
    rootMargin?: string;
  } = {}
): [RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1, ...options }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [options]);

  return [ref as RefObject<T>, isVisible];
}

// Animated section wrapper component
function AnimatedSection({ 
  children, 
  className = '', 
  delay = 0,
  direction = 'up'
}: { 
  children: React.ReactNode; 
  className?: string;
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'fade';
}) {
  const [ref, isVisible] = useScrollAnimation<HTMLDivElement>();
  
  const getTransform = () => {
    switch (direction) {
      case 'up': return 'translateY(40px)';
      case 'down': return 'translateY(-40px)';
      case 'left': return 'translateX(40px)';
      case 'right': return 'translateX(-40px)';
      case 'fade': return 'translateY(0)';
      default: return 'translateY(40px)';
    }
  };

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0) translateX(0)' : getTransform(),
        transition: `opacity 0.6s ease-out ${delay}ms, transform 0.6s ease-out ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function HomePage() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [expandedService, setExpandedService] = useState<number | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://norwebas.no';

  // SEO Configuration for Home Page
  useSEO({
    title: 'NorWeb AS | Webutvikling, Apputvikling & IT-hjelp',
    description: 'NorWeb AS tilbyr profesjonell webutvikling, apputvikling og IT-hjelp i Hamar. Vi lager nettsider og apper med SEO-optimalisering, responsivt design og vedlikehold. Fast pris fra Kr 990/mnd.',
    keywords: 'Norweb, webutvikling, apputvikling, IT-hjelp, nettside utvikling, nettsider, webdesign, lage nettside, app utvikling, IT support, webutvikling Hamar, responsivt design, SEO optimalisering',
    canonical: siteUrl,
    ogImage: 'https://static.readdy.ai/image/aeaa191b0ca02d38c9577b82b13368df/794dc186f9398e7ccf26db530a3381f1.png',
    structuredData: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'LocalBusiness',
          '@id': `${siteUrl}/#organization`,
          'name': 'NorWeb AS',
          'description': 'Profesjonell webutvikling, apputvikling og IT-hjelp i Hamar, Norge. Vi lager nettsider og apper for bedrifter, med SEO-optimalisering og løpende support.',
          'url': siteUrl,
          'logo': 'https://static.readdy.ai/image/aeaa191b0ca02d38c9577b82b13368df/794dc186f9398e7ccf26db530a3381f1.png',
          'image': 'https://static.readdy.ai/image/aeaa191b0ca02d38c9577b82b13368df/794dc186f9398e7ccf26db530a3381f1.png',
          'telephone': '+47 94 12 19 09',
          'email': 'kontakt@norwebas.no',
          'address': {
            '@type': 'PostalAddress',
            'addressLocality': 'Hamar',
            'addressRegion': 'Innlandet',
            'addressCountry': 'NO'
          },
          'geo': {
            '@type': 'GeoCoordinates',
            'latitude': '60.7945',
            'longitude': '11.0680'
          },
          'openingHoursSpecification': {
            '@type': 'OpeningHoursSpecification',
            'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            'opens': '08:30',
            'closes': '16:30'
          },
          'priceRange': 'Kr 990 - Kr 2490/mnd',
          'sameAs': []
        },
        {
          '@type': 'WebSite',
          'name': 'NorWeb AS - Webutvikling og Webdesign',
          'url': siteUrl,
          'potentialAction': {
            '@type': 'SearchAction',
            'target': `${siteUrl}/?s={search_term_string}`,
            'query-input': 'required name=search_term_string'
          }
        },
        {
          '@type': 'Service',
          'serviceType': ['Webutvikling', 'Apputvikling', 'IT-hjelp', 'Webdesign', 'Nettside utvikling', 'SEO optimalisering'],
          'provider': {
            '@type': 'LocalBusiness',
            'name': 'NorWeb AS'
          },
          'areaServed': {
            '@type': 'Country',
            'name': 'Norge'
          },
          'hasOfferCatalog': {
            '@type': 'OfferCatalog',
            'name': 'Webutvikling, Apputvikling og IT-hjelp',
            'itemListElement': [
              {
                '@type': 'Offer',
                'itemOffered': {
                  '@type': 'Service',
                  'name': 'Basis Nettside',
                  'description': 'Profesjonell nettside med opptil 5 sider, responsivt webdesign, SEO optimalisering'
                },
                'price': '990',
                'priceCurrency': 'NOK',
                'priceSpecification': {
                  '@type': 'UnitPriceSpecification',
                  'price': '990',
                  'priceCurrency': 'NOK',
                  'unitText': 'måned'
                }
              },
              {
                '@type': 'Offer',
                'itemOffered': {
                  '@type': 'Service',
                  'name': 'Standard Nettside',
                  'description': 'Avansert webutvikling med opptil 7 sider, SEO, prioritert support'
                },
                'price': '1490',
                'priceCurrency': 'NOK',
                'priceSpecification': {
                  '@type': 'UnitPriceSpecification',
                  'price': '1490',
                  'priceCurrency': 'NOK',
                  'unitText': 'måned'
                }
              },
              {
                '@type': 'Offer',
                'itemOffered': {
                  '@type': 'Service',
                  'name': 'Premium Nettside',
                  'description': 'Komplett webdesign med opptil 10 sider, full SEO-pakke, dedikert support'
                },
                'price': '2490',
                'priceCurrency': 'NOK',
                'priceSpecification': {
                  '@type': 'UnitPriceSpecification',
                  'price': '2490',
                  'priceCurrency': 'NOK',
                  'unitText': 'måned'
                }
              }
            ]
          }
        },
        {
          '@type': 'FAQPage',
          'mainEntity': [
            {
              '@type': 'Question',
              'name': 'Hvor lang tid tar det å lage en nettside?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Vanligvis 5-10 dager fra prosjektstart til lansering, avhengig av kompleksitet og omfang. Vi kan tilpasse raskere tidslinjer ved behov.'
              }
            },
            {
              '@type': 'Question',
              'name': 'Hva er inkludert i månedsprisen for webutvikling?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Hosting, SSL-sertifikat, sikkerhetsoppdateringer, backup, teknisk support og et antall innholdsoppdateringer per år basert på pakken din.'
              }
            },
            {
              '@type': 'Question',
              'name': 'Tilbyr NorWeb webdesign og SEO optimalisering?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Ja, alle våre nettsider inkluderer profesjonelt webdesign og grunnleggende SEO optimalisering for bedre synlighet i søkemotorer.'
              }
            },
            {
              '@type': 'Question',
              'name': 'Hva skjer etter at nettsiden er lansert?',
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': 'Vi tilbyr løpende support og vedlikehold. Du kan kontakte oss når som helst for hjelp, og vi sørger for at nettstedet ditt forblir sikkert og oppdatert.'
              }
            }
          ]
        }
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const initUnicorn = () => {
      if (window.UnicornStudio && window.UnicornStudio.init) {
        window.UnicornStudio.init();
      }
    };

    const timer = setTimeout(initUnicorn, 100);

    return () => {
      clearTimeout(timer);
      if (window.UnicornStudio && window.UnicornStudio.destroy) {
        window.UnicornStudio.destroy();
      }
    };
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isMobileMenuOpen]);

  const services = [
    {
      icon: 'ri-layout-4-line',
      title: t('services.service1Title'),
      description: t('services.service1Desc')
    },
    {
      icon: 'ri-smartphone-line',
      title: t('services.service2Title'),
      description: t('services.service2Desc')
    },
    {
      icon: 'ri-customer-service-2-line',
      title: t('services.service3Title'),
      description: t('services.service3Desc')
    },
    {
      icon: 'ri-search-eye-line',
      title: t('services.service4Title'),
      description: t('services.service4Desc')
    },
    {
      icon: 'ri-tools-line',
      title: t('services.service5Title'),
      description: t('services.service5Desc')
    }
  ];

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Handle keyboard navigation for services accordion
  const handleServiceKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setExpandedService(expandedService === index ? null : index);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans overflow-x-hidden max-w-[100vw]">
      {/* Skip to main content link for accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-fuchsia-600 focus:text-white focus:rounded-lg"
      >
        Hopp til hovedinnhold
      </a>

      {/* Navigation */}
      <header>
        <nav
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
            isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
          }`}
          aria-label="Hovednavigasjon"
        >
          <div className="w-full px-4 sm:px-6 lg:px-12 py-3">
            <div className="flex items-center justify-between">
              <a href="/" className="flex items-center gap-2 relative z-[60]" aria-label="NorWeb AS - Gå til forsiden">
                <img 
                  src={isScrolled || isMobileMenuOpen
                    ? "https://static.readdy.ai/image/aeaa191b0ca02d38c9577b82b13368df/794dc186f9398e7ccf26db530a3381f1.png"
                    : "https://static.readdy.ai/image/aeaa191b0ca02d38c9577b82b13368df/44a7a8c2159e827a3b36d551e672e0e2.png"
                  } 
                  alt="NorWeb AS - Webutvikling og Webdesign i Hamar" 
                  className="h-7 sm:h-8 w-auto"
                  width="100"
                  height="32"
                />
              </a>
              <ul className="hidden lg:flex items-center gap-6 list-none p-0 m-0">
                <li>
                  <a
                    href="#services"
                    className={`text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      isScrolled ? 'text-slate-600 hover:text-slate-900' : 'text-slate-300 hover:text-white'
                    }`}
                  >
                    {t('nav.services')}
                  </a>
                </li>
                <li>
                  <a
                    href="/analyse"
                    className={`text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      isScrolled ? 'text-slate-600 hover:text-slate-900' : 'text-slate-300 hover:text-white'
                    }`}
                  >
                    {t('nav.analyse')}
                  </a>
                </li>
                <li>
                  <a
                    href="/booking"
                    className={`text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      isScrolled ? 'text-slate-600 hover:text-slate-900' : 'text-slate-300 hover:text-white'
                    }`}
                  >
                    {t('nav.booking')}
                  </a>
                </li>
                <li>
                  <a
                    href="#pricing"
                    className={`text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      isScrolled ? 'text-slate-600 hover:text-slate-900' : 'text-slate-300 hover:text-white'
                    }`}
                  >
                    {t('nav.pricing')}
                  </a>
                </li>
                <li className={isScrolled ? '' : 'opacity-90'}>
                  <LanguageSwitcher />
                </li>
                <li>
                  <a
                    href="#contact-form"
                    className={`px-4 py-2 text-white text-xs font-medium rounded-md transition-all whitespace-nowrap cursor-pointer ${
                      isScrolled 
                        ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600' 
                        : 'bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600'
                    }`}
                  >
                    {t('nav.contact')}
                  </a>
                </li>
              </ul>
              <button 
                className="lg:hidden cursor-pointer z-[60] relative p-2"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-menu"
                aria-label={isMobileMenuOpen ? 'Lukk meny' : 'Åpne meny'}
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl ${isScrolled || isMobileMenuOpen ? 'text-slate-800' : 'text-white'}`} aria-hidden="true"></i>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          <div
            id="mobile-menu"
            className={`lg:hidden fixed top-0 left-0 right-0 h-[100vh] bg-white z-50 transition-transform duration-300 ${
              isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            role="dialog"
            aria-modal="true"
            aria-label="Mobilmeny"
          >
            <ul className="flex flex-col p-4 space-y-3 h-full overflow-y-auto pt-16 list-none m-0">
              <li>
                <a
                  href="#services"
                  onClick={() => handleNavClick('#services')}
                  className="text-base font-medium text-slate-600 hover:text-slate-900 py-2 border-b border-slate-200 cursor-pointer block"
                >
                  {t('nav.services')}
                </a>
              </li>
              <li>
                <a
                  href="/analyse"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-base font-medium text-slate-600 hover:text-slate-900 py-2 border-b border-slate-200 cursor-pointer block"
                >
                  {t('nav.analyse')}
                </a>
              </li>
              <li>
                <a
                  href="/booking"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-base font-medium text-slate-600 hover:text-slate-900 py-2 border-b border-slate-200 cursor-pointer block"
                >
                  {t('nav.booking')}
                </a>
              </li>
              <li>
                <a
                  href="#pricing"
                  onClick={() => handleNavClick('#pricing')}
                  className="text-base font-medium text-slate-600 hover:text-slate-900 py-2 border-b border-slate-200 cursor-pointer block"
                >
                  {t('nav.pricing')}
                </a>
              </li>
              <li className="py-2 border-b border-slate-200">
                <LanguageSwitcher />
              </li>
              <li>
                <a
                  href="#contact-form"
                  onClick={() => handleNavClick('#contact-form')}
                  className="px-4 py-2.5 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-medium rounded-md hover:from-violet-600 hover:to-fuchsia-600 transition-all whitespace-nowrap cursor-pointer text-sm"
                >
                  {t('nav.contact')}
                </a>
              </li>
            </ul>
          </div>
        </nav>
      </header>

      <main id="main-content">
        {/* Hero Section */}
        <section className="relative min-h-[85vh] sm:min-h-screen flex items-center overflow-hidden bg-slate-900" aria-labelledby="hero-title">
          <div className="absolute inset-0">
            <div 
              data-us-project="C27t3mQMXv15cgkgOGbY" 
              style={{ width: '100%', height: '100%' }}
              aria-hidden="true"
            ></div>
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
          </div>

          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 py-16 sm:py-20">
            <div className="max-w-3xl">
              <AnimatedSection delay={0} direction="up">
                <div className="mb-3 sm:mb-4">
                  <span className="inline-block w-10 h-1 rounded-full bg-gradient-to-r from-violet-400 to-fuchsia-400 mb-4 sm:mb-5"></span>
                  <h1 id="hero-title" className="font-bold text-white leading-[1.1] tracking-tight">
                    <span className="block text-3xl sm:text-3xl md:text-4xl lg:text-5xl mb-1 sm:mb-2">
                      {t('hero.title1')}
                    </span>
                    <span className="block text-3xl sm:text-3xl md:text-4xl lg:text-5xl bg-gradient-to-r from-violet-400 via-fuchsia-400 to-pink-400 bg-clip-text text-transparent pb-1">
                      {t('hero.title2')}
                    </span>
                  </h1>
                </div>
              </AnimatedSection>
              <AnimatedSection delay={150} direction="up">
                <p className="text-sm sm:text-base text-slate-300 mb-3 max-w-xl">
                  {t('hero.subtitle')}
                </p>
              </AnimatedSection>
              <AnimatedSection delay={300} direction="up">
                <ul className="list-none p-0 m-0">
                  <li className="flex items-start gap-2 text-slate-400 mb-2">
                    <i className="ri-checkbox-circle-fill text-fuchsia-400 flex-shrink-0 mt-0.5 text-sm" aria-hidden="true"></i>
                    <span className="text-xs sm:text-sm">
                      {t('hero.feature1')}
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-slate-400 mb-4 sm:mb-6">
                    <i className="ri-checkbox-circle-fill text-fuchsia-400 flex-shrink-0 mt-0.5 text-sm" aria-hidden="true"></i>
                    <span className="text-xs sm:text-sm">
                      {t('hero.feature2')}
                    </span>
                  </li>
                </ul>
              </AnimatedSection>
              <AnimatedSection delay={450} direction="up">
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-3 w-full">
                  <a
                    href="/booking"
                    className="flex items-center justify-center gap-2 px-6 py-3.5 sm:py-2.5 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold rounded-lg sm:rounded-md hover:from-violet-600 hover:to-fuchsia-600 transition-all whitespace-nowrap cursor-pointer text-center text-sm sm:text-sm shadow-lg shadow-fuchsia-500/25 sm:shadow-md"
                  >
                    <i className="ri-calendar-check-line text-base sm:text-sm" aria-hidden="true"></i>
                    {t('hero.btnBooking')}
                  </a>
                  <a
                    href="#contact-form"
                    className="flex items-center justify-center gap-2 px-6 py-3.5 sm:py-2.5 bg-white/10 text-white font-semibold rounded-lg sm:rounded-md border border-white/20 hover:bg-white/20 backdrop-blur-sm transition-all whitespace-nowrap cursor-pointer text-center text-sm sm:text-sm"
                  >
                    <i className="ri-mail-send-line text-base sm:text-sm" aria-hidden="true"></i>
                    {t('hero.btnContact')}
                  </a>
                </div>
              </AnimatedSection>
            </div>
          </div>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce" aria-hidden="true">
            <i className="ri-arrow-down-line text-xl text-fuchsia-400"></i>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-10 sm:py-12 lg:py-14 px-4 sm:px-6 lg:px-12 bg-white" aria-labelledby="why-choose-title">
          <div className="max-w-6xl mx-auto">
            <AnimatedSection direction="up">
              <h2 id="why-choose-title" className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-800 text-center mb-8 sm:mb-10 px-2">
                {t('whyChoose.title')}
              </h2>
            </AnimatedSection>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: t('whyChoose.quality'),
                  description: t('whyChoose.qualityDesc')
                },
                {
                  icon: 'ri-chat-smile-2-line',
                  title: t('whyChoose.communication'),
                  description: t('whyChoose.communicationDesc')
                },
                {
                  icon: 'ri-puzzle-line',
                  title: t('whyChoose.solutions'),
                  description: t('whyChoose.solutionsDesc')
                }
              ].map((item, index) => (
                <AnimatedSection key={index} delay={index * 150} direction="up">
                  <article className="text-center p-4 sm:p-5 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors h-full">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 bg-gradient-to-br from-violet-600 to-fuchsia-600 rounded-xl flex items-center justify-center" aria-hidden="true">
                      <i className={`${item.icon} text-lg sm:text-xl text-white`}></i>
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-slate-800 mb-2">{item.title}</h3>
                    <p className="text-slate-600 text-xs leading-relaxed">{item.description}</p>
                  </article>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Analyse Section */}
        <section className="py-10 sm:py-12 lg:py-14 px-4 sm:px-6 lg:px-12 bg-gradient-to-br from-slate-900 to-slate-800" id="analyse-section" aria-labelledby="analyse-section-title">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <AnimatedSection direction="left">
                <div>
                  <span className="inline-block px-3 py-1 bg-fuchsia-500/20 text-fuchsia-300 text-xs font-medium rounded-full mb-4">
                    {t('analyseSection.subtitle')}
                  </span>
                  <h2 id="analyse-section-title" className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-4">
                    {t('analyseSection.title')}
                  </h2>
                  <p className="text-slate-300 text-sm leading-relaxed mb-6">
                    {t('analyseSection.description')}
                  </p>
                  <div className="flex flex-wrap gap-4 mb-6">
                    <div className="flex items-center gap-2">
                      <i className="ri-flashlight-line text-fuchsia-400" aria-hidden="true"></i>
                      <span className="text-slate-300 text-xs">{t('analyseSection.feature1')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-lock-unlock-line text-fuchsia-400" aria-hidden="true"></i>
                      <span className="text-slate-300 text-xs">{t('analyseSection.feature2')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-file-chart-line text-fuchsia-400" aria-hidden="true"></i>
                      <span className="text-slate-300 text-xs">{t('analyseSection.feature3')}</span>
                    </div>
                  </div>
                  <a
                    href="/analyse"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-medium rounded-md hover:from-violet-600 hover:to-fuchsia-600 transition-all whitespace-nowrap cursor-pointer text-sm"
                  >
                    {t('analyseSection.btn')}
                    <i className="ri-arrow-right-line" aria-hidden="true"></i>
                  </a>
                </div>
              </AnimatedSection>
              <AnimatedSection direction="right" delay={200}>
                <div className="relative">
                  <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400 text-xs">Hastighet</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div className="w-4/5 h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-full"></div>
                          </div>
                          <span className="text-fuchsia-400 text-xs font-medium">85%</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400 text-xs">SEO</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div className="w-3/4 h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-full"></div>
                          </div>
                          <span className="text-fuchsia-400 text-xs font-medium">72%</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400 text-xs">Mobilvennlig</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div className="w-[90%] h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-full"></div>
                          </div>
                          <span className="text-fuchsia-400 text-xs font-medium">92%</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400 text-xs">Sikkerhet</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div className="w-[95%] h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-full"></div>
                          </div>
                          <span className="text-fuchsia-400 text-xs font-medium">95%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 rounded-full blur-xl" aria-hidden="true"></div>
                  <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-br from-fuchsia-500/20 to-violet-500/20 rounded-full blur-xl" aria-hidden="true"></div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-10 sm:py-12 lg:py-14 px-4 sm:px-6 lg:px-12 bg-slate-50" id="services" aria-labelledby="services-title">
          <div className="max-w-6xl mx-auto">
            <AnimatedSection direction="up">
              <h2 id="services-title" className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-800 text-center mb-8 sm:mb-10 px-2">
                {t('services.title')}
              </h2>
            </AnimatedSection>
            <div className="space-y-2 sm:space-y-3" role="list">
              {services.map((service, index) => (
                <AnimatedSection key={index} delay={index * 100} direction="left">
                  <article className="bg-white rounded-lg shadow-sm overflow-hidden transition-all" role="listitem">
                    <button
                      onClick={() => setExpandedService(expandedService === index ? null : index)}
                      onKeyDown={(e) => handleServiceKeyDown(e, index)}
                      className="w-full p-3 sm:p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
                      aria-expanded={expandedService === index}
                      aria-controls={`service-content-${index}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-violet-600 to-fuchsia-600 rounded-lg flex items-center justify-center flex-shrink-0" aria-hidden="true">
                          <i className={`${service.icon} text-base sm:text-lg text-white`}></i>
                        </div>
                        <h3 className="text-xs sm:text-sm font-semibold text-slate-800 text-left">{service.title}</h3>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <span className="text-xs text-fuchsia-600 font-medium hidden md:block">{t('services.seeDetails')}</span>
                        <i
                          className={`ri-arrow-down-s-line text-lg sm:text-xl text-slate-400 transition-transform ${
                            expandedService === index ? 'rotate-180' : ''
                          }`}
                          aria-hidden="true"
                        ></i>
                      </div>
                    </button>
                    <div
                      id={`service-content-${index}`}
                      className={`overflow-hidden transition-all duration-300 ${
                        expandedService === index ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'
                      }`}
                      role="region"
                      aria-labelledby={`service-title-${index}`}
                    >
                      <div className="px-3 sm:px-4 pb-3 sm:pb-4 lg:pl-16">
                        <p className="text-slate-600 leading-relaxed text-xs sm:text-sm">{service.description}</p>
                      </div>
                    </div>
                  </article>
                </AnimatedSection>
              ))}
            </div>
            <AnimatedSection delay={600} direction="up">
              <div className="text-center mt-6 sm:mt-8">
                <a
                  href="#contact-form"
                  className="inline-block px-5 py-2.5 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-medium rounded-md hover:from-violet-600 hover:to-fuchsia-600 transition-all whitespace-nowrap cursor-pointer text-xs sm:text-sm"
                >
                  {t('services.btnAll')}
                </a>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-10 sm:py-12 lg:py-14 px-4 sm:px-6 lg:px-12 bg-white" id="process" aria-labelledby="process-title">
          <div className="max-w-6xl mx-auto">
            <AnimatedSection direction="up">
              <h2 id="process-title" className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-800 text-center mb-2 px-2">
                {t('process.title')}
              </h2>
              <p className="text-xs sm:text-sm text-slate-600 text-center mb-8 sm:mb-10 max-w-xl mx-auto px-4">
                {t('process.subtitle')}
              </p>
            </AnimatedSection>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {[
                {
                  num: '01',
                  title: t('process.step1'),
                  desc: t('process.step1Desc')
                },
                {
                  num: '02',
                  title: t('process.step2'),
                  desc: t('process.step2Desc')
                },
                {
                  num: '03',
                  title: t('process.step3'),
                  desc: t('process.step3Desc')
                },
                {
                  num: '04',
                  title: t('process.step4'),
                  desc: t('process.step4Desc')
                },
                {
                  num: '05',
                  title: t('process.step5'),
                  desc: t('process.step5Desc')
                },
                {
                  num: '06',
                  title: t('process.step6'),
                  desc: t('process.step6Desc')
                }
              ].map((step, index) => (
                <AnimatedSection key={index} delay={index * 100} direction="up">
                  <article className="relative bg-white rounded-xl p-4 sm:p-5 shadow-sm hover:shadow-md transition-all h-full">
                    <span className="text-3xl sm:text-4xl font-bold text-slate-100 absolute top-3 right-3" aria-hidden="true">{step.num}</span>
                    <div className="relative z-10">
                      <h3 className="text-sm sm:text-base font-bold text-slate-800 mb-2 pr-8">
                        <a href="#contact-form" className="hover:text-fuchsia-600 transition-colors">{step.title}</a>
                      </h3>
                      <p className="text-slate-600 text-xs leading-relaxed">{step.desc}</p>
                    </div>
                  </article>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-10 sm:py-12 lg:py-14 px-4 sm:px-6 lg:px-12 bg-gradient-to-br from-violet-900 to-fuchsia-900" aria-labelledby="features-title">
          <div className="max-w-6xl mx-auto">
            <AnimatedSection direction="up">
              <h2 id="features-title" className="text-xl sm:text-2xl md:text-3xl font-bold text-white text-center mb-8 sm:mb-10 px-2">{t('features.title')}</h2>
            </AnimatedSection>
            <ul className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 list-none p-0 m-0">
              {[
                { icon: 'ri-check-double-line', text: t('features.feature1') },
                { icon: 'ri-server-line', text: t('features.feature2') },
                { icon: 'ri-search-line', text: t('features.feature3') },
                { icon: 'ri-palette-line', text: t('features.feature4') },
                { icon: 'ri-smartphone-line', text: t('features.feature5') },
                { icon: 'ri-shield-check-line', text: t('features.feature6') },
                { icon: 'ri-speed-line', text: t('features.feature7') },
                { icon: 'ri-customer-service-line', text: t('features.feature8') }
              ].map((feature, index) => (
                <AnimatedSection key={index} delay={index * 75} direction="fade">
                  <li className="flex items-center gap-2 p-2.5 sm:p-3 bg-white/10 rounded-lg backdrop-blur-sm">
                    <i className={`${feature.icon} text-base sm:text-lg text-fuchsia-300 flex-shrink-0`} aria-hidden="true"></i>
                    <span className="text-[10px] sm:text-xs font-medium text-white">{feature.text}</span>
                  </li>
                </AnimatedSection>
              ))}
            </ul>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-10 sm:py-12 lg:py-14 px-4 sm:px-6 lg:px-12 bg-white" id="pricing" aria-labelledby="pricing-title">
          <div className="max-w-6xl mx-auto">
            <AnimatedSection direction="up">
              <h2 id="pricing-title" className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-800 text-center mb-2 px-2">
                {t('pricing.title')}
              </h2>
              <p className="text-xs sm:text-sm text-slate-600 text-center mb-8 sm:mb-10 max-w-xl mx-auto px-4">
                {t('pricing.subtitle')}
              </p>
            </AnimatedSection>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
              {[
                {
                  name: t('pricing.basis'),
                  price: '990',
                  setup: '4 490',
                  pages: 5,
                  updates: 2,
                  features: [
                    t('pricing.feature4Basis'),
                    t('pricing.feature5'),
                    t('pricing.feature6Basis')
                  ],
                  description: t('pricing.basisDesc')
                },
                {
                  name: t('pricing.standard'),
                  price: '1 490',
                  setup: '4 490',
                  pages: 10,
                  updates: 5,
                  features: [
                    t('pricing.feature4Standard'),
                    t('pricing.feature5'),
                    t('pricing.feature6Standard')
                  ],
                  description: t('pricing.standardDesc'),
                  popular: true
                },
                {
                  name: t('pricing.premium'),
                  price: '2 490',
                  setup: '4 490',
                  pages: 20,
                  updates: 8,
                  features: [
                    t('pricing.feature7Premium'),
                    t('pricing.feature4Premium'),
                    t('pricing.feature5'),
                    t('pricing.feature6Premium')
                  ],
                  description: t('pricing.premiumDesc')
                }
              ].map((plan, index) => (
                <AnimatedSection key={index} delay={index * 150} direction="up">
                  <article
                    className={`relative bg-white rounded-xl p-4 sm:p-5 transition-all h-full flex flex-col ${
                      plan.popular ? 'ring-2 ring-fuchsia-500 shadow-lg md:scale-105' : 'shadow-md hover:shadow-lg border border-slate-100'
                    }`}
                    aria-labelledby={`plan-${index}-title`}
                  >
                    {plan.popular && (
                      <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-[10px] sm:text-xs font-bold rounded-full whitespace-nowrap">
                        {t('pricing.mostPopular')}
                      </div>
                    )}
                    <div className="text-center mb-4">
                      <h3 id={`plan-${index}-title`} className="text-base sm:text-lg font-bold text-slate-800 mb-2">{plan.name}</h3>
                      <div className="mb-1">
                        <span className="text-xl sm:text-2xl font-bold text-slate-800">Kr {plan.price}</span>
                        <span className="text-xs text-slate-500"> {t('pricing.perMonth')}</span>
                      </div>
                      <p className="text-[10px] text-slate-400">{t('pricing.setupFee', { amount: `Kr ${plan.setup}` })}</p>
                    </div>
                    <ul className="space-y-2 mb-4 flex-grow list-none p-0 m-0">
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-sm text-fuchsia-500 flex-shrink-0 mt-0.5" aria-hidden="true"></i>
                        <span className="text-[11px] sm:text-xs text-slate-600">{t('pricing.feature1', { pages: plan.pages })}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-sm text-fuchsia-500 flex-shrink-0 mt-0.5" aria-hidden="true"></i>
                        <span className="text-[11px] sm:text-xs text-slate-600">{t('pricing.feature2')}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-checkbox-circle-fill text-sm text-fuchsia-500 flex-shrink-0 mt-0.5" aria-hidden="true"></i>
                        <span className="text-[11px] sm:text-xs text-slate-600">{t('pricing.feature3', { updates: plan.updates })}</span>
                      </li>
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-sm text-fuchsia-500 flex-shrink-0 mt-0.5" aria-hidden="true"></i>
                          <span className="text-[11px] sm:text-xs text-slate-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-[10px] text-slate-400 italic text-center">{plan.description}</p>
                  </article>
                </AnimatedSection>
              ))}
            </div>
            <AnimatedSection delay={500} direction="up">
              <p className="text-center text-[10px] sm:text-xs text-slate-500 mt-6 sm:mt-8 px-4">
                {t('pricing.disclaimer')}
              </p>
            </AnimatedSection>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-10 sm:py-12 lg:py-14 px-4 sm:px-6 lg:px-12 bg-gradient-to-br from-violet-900 to-fuchsia-900" aria-labelledby="cta-title">
          <AnimatedSection direction="up">
            <div className="max-w-3xl mx-auto text-center">
              <h2 id="cta-title" className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3 px-2">{t('cta.title')}</h2>
              <p className="text-violet-200 text-xs sm:text-sm mb-5 max-w-xl mx-auto px-4">
                {t('cta.subtitle')}
              </p>
              <a
                href="/booking"
                className="inline-block px-6 py-2.5 bg-white text-violet-900 font-medium rounded-md hover:bg-violet-50 transition-all whitespace-nowrap cursor-pointer text-xs sm:text-sm"
              >
                {t('cta.btn')}
              </a>
            </div>
          </AnimatedSection>
        </section>

        {/* Support Section */}
        <section className="py-10 sm:py-12 lg:py-14 px-4 sm:px-6 lg:px-12 bg-white" aria-labelledby="support-title">
          <div className="max-w-6xl mx-auto">
            <AnimatedSection direction="up">
              <h2 id="support-title" className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-800 text-center mb-2 px-2">{t('support.title')}</h2>
              <p className="text-xs sm:text-sm text-slate-600 text-center mb-8 sm:mb-10 max-w-xl mx-auto px-4">
                {t('support.subtitle')}
              </p>
            </AnimatedSection>

            {/* Support Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 max-w-4xl mx-auto">
              {/* Email Support */}
              <AnimatedSection delay={0} direction="up">
                <article className="bg-white rounded-lg p-4 sm:p-5 shadow-md hover:shadow-lg transition-all duration-300 text-center h-full flex flex-col">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-full flex items-center justify-center mx-auto mb-3" aria-hidden="true">
                    <i className="ri-mail-line text-lg sm:text-xl text-white"></i>
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-2">
                    {t('support.email.title')}
                  </h3>
                  <p className="text-xs text-gray-600 mb-3 flex-grow">
                    {t('support.email.description')}
                  </p>
                  <a
                    href="mailto:kontakt@norwebas.no"
                    className="inline-block px-4 py-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-md font-medium hover:from-violet-700 hover:to-fuchsia-700 transition-all duration-300 whitespace-nowrap cursor-pointer text-xs"
                    aria-label="Send e-post til kontakt@norwebas.no"
                  >
                    {t('support.email.action')}
                  </a>
                </article>
              </AnimatedSection>

              {/* Phone Support */}
              <AnimatedSection delay={150} direction="up">
                <article className="bg-white rounded-lg p-4 sm:p-5 shadow-md hover:shadow-lg transition-all duration-300 text-center h-full flex flex-col">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-full flex items-center justify-center mx-auto mb-3" aria-hidden="true">
                    <i className="ri-phone-line text-lg sm:text-xl text-white"></i>
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-2">
                    {t('support.phone.title')}
                  </h3>
                  <p className="text-xs text-gray-600 mb-3 flex-grow">
                    {t('support.phone.description')}
                  </p>
                  <a
                    href="tel:+4794121909"
                    className="inline-block px-4 py-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-md font-medium hover:from-violet-700 hover:to-fuchsia-700 transition-all duration-300 whitespace-nowrap cursor-pointer text-xs"
                    aria-label="Ring +47 94 12 19 09"
                  >
                    {t('support.phone.action')}
                  </a>
                </article>
              </AnimatedSection>

              {/* AI Assistant */}
              <AnimatedSection delay={300} direction="up">
                <article className="bg-white rounded-lg p-4 sm:p-5 shadow-md hover:shadow-lg transition-all duration-300 text-center h-full flex flex-col">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-full flex items-center justify-center mx-auto mb-3" aria-hidden="true">
                    <i className="ri-robot-line text-lg sm:text-xl text-white"></i>
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-gray-900 mb-2">
                    {t('support.ai.title')}
                  </h3>
                  <p className="text-xs text-gray-600 mb-3 flex-grow">
                    {t('support.ai.description')}
                  </p>
                  <button
                    onClick={() => {
                      const vapiButton = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
                      if (vapiButton) {
                        vapiButton.click();
                        return;
                      }
                      window.dispatchEvent(new CustomEvent('openAIChatbot'));
                    }}
                    className="inline-block px-4 py-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-md font-medium hover:from-violet-700 hover:to-fuchsia-700 transition-all duration-300 whitespace-nowrap cursor-pointer text-xs"
                    aria-label="Åpne AI chat assistent"
                  >
                    {t('support.ai.action')}
                  </button>
                </article>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section id="contact-form" className="py-10 sm:py-12 lg:py-14 px-4 sm:px-6 lg:px-12 bg-slate-50" aria-labelledby="contact-title">
          <div className="max-w-3xl mx-auto">
            <AnimatedSection>
              <div className="text-center mb-5 sm:mb-6">
                <h2 id="contact-title" className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 mb-2 px-2">
                  {t('contactForm.title')}
                </h2>
                <p className="text-xs sm:text-sm text-slate-600 px-4">
                  {t('contactForm.subtitle')}
                </p>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={200}>
              <div className="bg-white rounded-xl shadow-lg p-4 sm:p-5">
                <form 
                  id="contact-form-element"
                  data-readdy-form
                  action="https://readdy.ai/api/form/d64ujjc5l058qvghfpag"
                  method="POST"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const form = e.currentTarget;
                    const formData = new FormData(form);
                    
                    const requiredFields = ['navn', 'etternavn', 'email', 'telefon', 'virksomhet', 'melding'];
                    let isValid = true;
                    
                    requiredFields.forEach(field => {
                      const value = formData.get(field);
                      if (!value || (typeof value === 'string' && value.trim() === '')) {
                        isValid = false;
                      }
                    });
                    
                    if (!isValid) {
                      const errorDiv = document.getElementById('form-error');
                      if (errorDiv) {
                        errorDiv.textContent = t('contactForm.validationError');
                        errorDiv.classList.remove('hidden');
                      }
                      return;
                    }
                    
                    const email = formData.get('email') as string;
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                      const errorDiv = document.getElementById('form-error');
                      if (errorDiv) {
                        errorDiv.textContent = t('contactForm.emailError');
                        errorDiv.classList.remove('hidden');
                      }
                      return;
                    }
                    
                    const melding = formData.get('melding') as string;
                    if (melding.length > 500) {
                      const errorDiv = document.getElementById('form-error');
                      if (errorDiv) {
                        errorDiv.textContent = t('contactForm.lengthError');
                        errorDiv.classList.remove('hidden');
                      }
                      return;
                    }
                    
                    const errorDiv = document.getElementById('form-error');
                    if (errorDiv) {
                      errorDiv.classList.add('hidden');
                    }
                    
                    const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement;
                    const originalText = submitButton.textContent;
                    submitButton.disabled = true;
                    submitButton.textContent = t('contactForm.btnSending');
                    submitButton.setAttribute('aria-busy', 'true');
                    
                    fetch('https://readdy.ai/api/form/d64ujjc5l058qvghfpag', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                      },
                      body: new URLSearchParams(formData as any).toString()
                    })
                    .then(response => {
                      const successDiv = document.getElementById('form-success');
                      if (response.ok) {
                        if (successDiv) {
                          successDiv.textContent = t('contactForm.successMessage');
                          successDiv.classList.remove('hidden');
                        }
                        form.reset();
                      } else {
                        const errorDiv = document.getElementById('form-error');
                        if (errorDiv) {
                          errorDiv.textContent = t('contactForm.errorMessage');
                          errorDiv.classList.remove('hidden');
                        }
                      }
                    })
                    .catch(() => {
                      const errorDiv = document.getElementById('form-error');
                      if (errorDiv) {
                        errorDiv.textContent = t('contactForm.errorMessage');
                        errorDiv.classList.remove('hidden');
                      }
                    })
                    .finally(() => {
                      submitButton.disabled = false;
                      submitButton.textContent = originalText;
                      submitButton.setAttribute('aria-busy', 'false');
                    });
                  }}
                  noValidate
                >
                  <div id="form-error" className="hidden mb-3 p-2.5 bg-red-50 border border-red-200 text-red-700 rounded-md text-xs" role="alert" aria-live="polite"></div>
                  <div id="form-success" className="hidden mb-3 p-2.5 bg-green-50 border border-green-200 text-green-700 rounded-md text-xs" role="status" aria-live="polite"></div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div>
                      <label htmlFor="navn" className="block text-xs font-medium text-slate-700 mb-1">
                        {t('contactForm.firstName')} <span aria-hidden="true">*</span>
                      </label>
                      <input
                        type="text"
                        id="navn"
                        name="navn"
                        required
                        autoComplete="given-name"
                        className="w-full px-3 py-2 rounded-md border border-slate-300 focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent outline-none transition-all text-sm"
                        placeholder={t('contactForm.placeholderFirstName')}
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label htmlFor="etternavn" className="block text-xs font-medium text-slate-700 mb-1">
                        {t('contactForm.lastName')} <span aria-hidden="true">*</span>
                      </label>
                      <input
                        type="text"
                        id="etternavn"
                        name="etternavn"
                        required
                        autoComplete="family-name"
                        className="w-full px-3 py-2 rounded-md border border-slate-300 focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent outline-none transition-all text-sm"
                        placeholder={t('contactForm.placeholderLastName')}
                        aria-required="true"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div>
                      <label htmlFor="email" className="block text-xs font-medium text-slate-700 mb-1">
                        {t('contactForm.email')} <span aria-hidden="true">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        autoComplete="email"
                        className="w-full px-3 py-2 rounded-md border border-slate-300 focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent outline-none transition-all text-sm"
                        placeholder={t('contactForm.placeholderEmail')}
                        aria-required="true"
                      />
                    </div>
                    <div>
                      <label htmlFor="telefon" className="block text-xs font-medium text-slate-700 mb-1">
                        {t('contactForm.phone')} <span aria-hidden="true">*</span>
                      </label>
                      <input
                        type="tel"
                        id="telefon"
                        name="telefon"
                        required
                        autoComplete="tel"
                        className="w-full px-3 py-2 rounded-md border border-slate-300 focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent outline-none transition-all text-sm"
                        placeholder={t('contactForm.placeholderPhone')}
                        aria-required="true"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-3 sm:mb-4">
                    <label htmlFor="virksomhet" className="block text-xs font-medium text-slate-700 mb-1">
                      {t('contactForm.business')} <span aria-hidden="true">*</span>
                    </label>
                    <input
                      type="text"
                      id="virksomhet"
                      name="virksomhet"
                      required
                      autoComplete="organization"
                      className="w-full px-3 py-2 rounded-md border border-slate-300 focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent outline-none transition-all text-sm"
                      placeholder={t('contactForm.placeholderBusiness')}
                      aria-required="true"
                    />
                  </div>
                  
                  <div className="mb-3 sm:mb-4">
                    <label htmlFor="melding" className="block text-xs font-medium text-slate-700 mb-1">
                      {t('contactForm.message')} <span aria-hidden="true">*</span>
                    </label>
                    <textarea
                      id="melding"
                      name="melding"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2 rounded-md border border-slate-300 focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent outline-none transition-all text-sm resize-none"
                      placeholder={t('contactForm.placeholderMessage')}
                      aria-required="true"
                    ></textarea>
                    <p className="text-[10px] text-slate-500 mt-0.5">{t('contactForm.maxChars')}</p>
                  </div>
                  
                  <button
                    type="submit"
                    className="w-full bg-fuchsia-600 text-white py-2.5 rounded-md font-medium hover:bg-fuchsia-700 transition-colors whitespace-nowrap cursor-pointer text-xs focus:ring-2 focus:ring-fuchsia-500 focus:ring-offset-2"
                    aria-busy="false"
                  >
                    {t('contactForm.btnSubmit')}
                  </button>
                </form>
              </div>
            </AnimatedSection>
          </div>
        </section>

      </main>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-8 sm:py-10 px-4 sm:px-6 lg:px-12" role="contentinfo">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <img 
                  src="https://static.readdy.ai/image/aeaa191b0ca02d38c9577b82b13368df/44a7a8c2159e827a3b36d551e672e0e2.png" 
                  alt="NorWeb AS logo" 
                  className="h-7 sm:h-8 w-auto"
                  width="100"
                  height="32"
                  loading="lazy"
                />
              </div>
              <p className="text-slate-300 text-xs leading-relaxed">
                {t('footer.description')}
              </p>
            </div>
            <nav aria-label="Tjenester">
              <h2 className="text-sm font-bold mb-2 sm:mb-3">{t('footer.servicesTitle')}</h2>
              <ul className="space-y-1.5 list-none p-0 m-0">
                <li>
                  <a href="#services" className="text-slate-300 hover:text-white transition-colors text-xs cursor-pointer">
                    {t('footer.service1')}
                  </a>
                </li>
                <li>
                  <a href="#services" className="text-slate-300 hover:text-white transition-colors text-xs cursor-pointer">
                    {t('footer.service2')}
                  </a>
                </li>
                <li>
                  <a href="#services" className="text-slate-300 hover:text-white transition-colors text-xs cursor-pointer">
                    {t('footer.service3')}
                  </a>
                </li>
                <li>
                  <a href="#services" className="text-slate-300 hover:text-white transition-colors text-xs cursor-pointer">
                    {t('footer.service4')}
                  </a>
                </li>
              </ul>
            </nav>
            <nav aria-label="Selskap">
              <h2 className="text-sm font-bold mb-2 sm:mb-3">{t('footer.companyTitle')}</h2>
              <ul className="space-y-1.5 list-none p-0 m-0">
                <li>
                  <a href="#" className="text-slate-300 hover:text-white transition-colors text-xs cursor-pointer">
                    {t('footer.aboutUs')}
                  </a>
                </li>
                <li>
                  <a href="#" className="text-slate-300 hover:text-white transition-colors text-xs cursor-pointer">
                    {t('footer.projects')}
                  </a>
                </li>
                <li>
                  <a href="#contact-form" className="text-slate-300 hover:text-white transition-colors text-xs cursor-pointer">
                    {t('footer.contact')}
                  </a>
                </li>
              </ul>
            </nav>
            <div>
              <h2 className="text-sm font-bold mb-2 sm:mb-3">{t('footer.contactTitle')}</h2>
              <address className="not-italic">
                <ul className="space-y-1.5 list-none p-0 m-0">
                  <li className="flex items-center gap-2 text-slate-300 text-xs">
                    <i className="ri-mail-line text-fuchsia-400 flex-shrink-0" aria-hidden="true"></i>
                    <a href="mailto:kontakt@norwebas.no" className="break-all hover:text-white transition-colors">kontakt@norwebas.no</a>
                  </li>
                  <li className="flex items-center gap-2 text-slate-300 text-xs">
                    <i className="ri-phone-line text-fuchsia-400 flex-shrink-0" aria-hidden="true"></i>
                    <a href="tel:+4794121909" className="hover:text-white transition-colors">+47 94 12 19 09</a>
                  </li>
                  <li className="flex items-center gap-2 text-slate-300 text-xs">
                    <i className="ri-map-pin-line text-fuchsia-400 flex-shrink-0" aria-hidden="true"></i>
                    <span>Brugata 3A, 2380 Brumunddal</span>
                  </li>
                </ul>
              </address>
            </div>
          </div>
          <div className="border-t border-slate-700 pt-5">
            <p className="text-xs text-slate-400 text-center">
              {t('footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
